/**
 * THE FILE — the type roles.
 *
 * There is one display face and one operating face, and the division between
 * them is a rule rather than a preference. Fraunces (`font-serif`) appears in
 * exactly three roles: the title of a file, the interpretation statement, and
 * the dock's heading. Everywhere else — headings included — is IBM Plex Sans.
 * A serif section heading would make the file look like a brochure about
 * accounting rather than a piece of accounting.
 */
import { clsx } from "clsx";
import type { CSSProperties, ReactNode } from "react";

/**
 * Fraunces is a variable face with an optical-size axis, and its default optical
 * size is set for small text. At display sizes the low-opsz cut looks blunt, so
 * every serif role pins `opsz 72`. It lives here once so no call site can
 * forget it and get a subtly different title.
 */
export const SERIF_OPSZ: CSSProperties = { fontVariationSettings: '"opsz" 72' };

export interface LabelProps {
  children: ReactNode;
  className?: string | undefined;
  /** A label is often a `dt` or sits inside a heading; the tag follows the role. */
  as?: "div" | "span" | "dt" | "h2" | "h3" | undefined;
}

/** type.label — 11px caps in stone. Group and section labels, and nothing else. */
export function Label({ children, className, as: Tag = "div" }: LabelProps) {
  return (
    <Tag className={clsx("text-[11px] font-medium uppercase tracking-[0.09em] text-stone", className)}>
      {children}
    </Tag>
  );
}

export interface SectionHeadProps {
  children: ReactNode;
  /** The quiet right-hand fact: a round number, a count, a date. Never an action. */
  aside?: ReactNode;
  id?: string | undefined;
  className?: string | undefined;
}

/** type.heading — a heading inside the file. 15/600, and never a serif. */
export function SectionHead({ children, aside, id, className }: SectionHeadProps) {
  return (
    <div className={clsx("mb-3 flex items-baseline justify-between gap-4", className)}>
      <h2 id={id} className="text-[15px] font-semibold text-ink">{children}</h2>
      {aside && <div className="text-[13px] text-stone">{aside}</div>}
    </div>
  );
}

export interface SectionProps {
  children: ReactNode;
  className?: string | undefined;
  id?: string | undefined;
}

/**
 * The space between sections of the file — and it is space, never a box.
 *
 * Boxing every section means everything is a region, so nothing is grouped:
 * common region only groups by contrast. A section of one file is a paragraph,
 * not a separable object, so it gets air above it and no border at all.
 */
export function Section({ children, className, id }: SectionProps) {
  return <section id={id} className={clsx("pt-7", className)}>{children}</section>;
}

export interface CitationProps {
  /**
   * The MACHINE VALUE behind the words, one hover away (R5.1).
   *
   * Where a citation reads a governed enum for a person — "Computed by Leveri
   * from the records" rather than DETERMINISTIC_COMPUTATION — the value the
   * record actually holds travels here. The screen shows the sentence; the
   * provenance is never lost, only moved out of the reading line.
   */
  title?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
  as?: "span" | "div" | undefined;
}

/**
 * type.ref — a citation: file › sheet › row. Only the segments that exist.
 *
 * Set in mono because it is a locator an accountant will retype into a
 * workbook, and a locator you cannot transcribe character for character is not
 * a citation. Every figure in this product carries one; a value with no stated
 * source is not shown at all.
 */
export function Citation({ children, className, title, as: Tag = "span" }: CitationProps) {
  return <Tag className={clsx("font-mono text-[12px] text-ink-soft", className)} title={title}>{children}</Tag>;
}

export interface RecordNameProps {
  children: ReactNode;
  className?: string | undefined;
}

/** A record's own filename, set as a reference rather than as prose. */
export function RecordName({ children, className }: RecordNameProps) {
  return <span className={clsx("font-mono text-[13px] text-ink", className)}>{children}</span>;
}

export interface StatementProps {
  children: ReactNode;
  className?: string | undefined;
}

/**
 * type.statement — Fraunces italic, 21px. The interpretation statement, and
 * ONLY it.
 *
 * This is the one sentence on a screen that says what Leveri understands the
 * work to be. It is set apart typographically because a reader must be able to
 * tell, without reading carefully, which sentence is the claim and which are
 * the supporting facts. Using this treatment for anything else would spend
 * that distinction on something that has not earned it.
 */
export function Statement({ children, className }: StatementProps) {
  return (
    <p className={clsx("font-serif text-[21px] font-normal italic leading-[1.4] text-ink", className)} style={SERIF_OPSZ}>
      {children}
    </p>
  );
}

export interface FileTitleProps {
  /** The caps line above — what kind of thing this is. */
  kicker?: ReactNode;
  children: ReactNode;
  /** The quiet line beneath: client, period, currency. Tabular, because it holds dates. */
  meta?: ReactNode;
  /** 32 is the file's own title; 28 is a title inside a narrower measure. */
  size?: 32 | 28 | undefined;
  className?: string | undefined;
}

/** type.display.2 — the title of a file, with its kicker and its meta line. */
export function FileTitle({ kicker, children, meta, size = 32, className }: FileTitleProps) {
  return (
    <div className={className}>
      {kicker && <Label>{kicker}</Label>}
      <h1
        // text-wrap:balance so a two-line title breaks where a typesetter would,
        // not wherever the container edge happens to fall.
        className={clsx(
          "mt-2 font-serif font-medium leading-[1.15] tracking-[-0.015em] text-ink [text-wrap:balance]",
          size === 28 ? "text-[28px]" : "text-[32px]"
        )}
        style={SERIF_OPSZ}
      >
        {children}
      </h1>
      {meta && <div className="mt-1.5 text-[13px] text-stone [font-variant-numeric:tabular-nums]">{meta}</div>}
    </div>
  );
}
